import { useMemo, useCallback } from 'react';

import { isExternalPath } from 'modules/common/utils/isExternalPath';
import { useCheckAccountPermission } from 'modules/account/hooks/useCheckAccountPermission';
import { useThemes } from 'uiKit/Theme/hook/useThemes';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAuth } from 'modules/auth/hooks/useAuth';

import { NavigationItem } from './types';

export interface IUseBaseNavButtonParams {
  item: NavigationItem;
  isMobileSideBar?: boolean;
  handleSidebarClose?: () => void;
}

export const useBaseNavButton = ({
  handleSidebarClose,
  isMobileSideBar = false,
  item,
}: IUseBaseNavButtonParams) => {
  const { isLoggedIn } = useAuth();
  const { permissionsInitialized } = useAccount();

  const { isLightTheme } = useThemes();

  const {
    StartIcon,
    amount,
    href,
    internalItems,
    isActive,
    isComingSoon,
    isDisabled,
    isNew,
    isNotLinkItem,
    label,
    onAccessDeniedClick,
    requiresPermission,
  } = item;

  const hasPremission = useCheckAccountPermission({
    permission: requiresPermission,
  });

  const hasAccess = !isLoggedIn || (permissionsInitialized && hasPremission);

  const isExternalHref = useMemo(() => href && isExternalPath(href), [href]);

  const isAccordionButton = useMemo(
    () => internalItems && internalItems.length > 0,
    [internalItems],
  );

  const handleCloseOnClick = useCallback(() => {
    if (handleSidebarClose && isMobileSideBar) {
      handleSidebarClose();
    }
  }, [handleSidebarClose, isMobileSideBar]);

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      handleCloseOnClick();

      if (hasAccess) {
        item.onClick?.(event);
      } else {
        event.preventDefault();

        onAccessDeniedClick?.();
      }
    },
    [hasAccess, handleCloseOnClick, item, onAccessDeniedClick],
  );

  return {
    amount,
    hasAccess,
    isLightTheme,
    isExternalHref,
    isNotLinkItem,
    isAccordionButton,
    isComingSoon,
    isDisabled,
    isActive,
    isNew,
    label,
    onClick,
    handleCloseOnClick,
    StartIcon,
  };
};
